import React, { useEffect } from 'react';

import { Navigate, Outlet } from 'react-router-dom';

import Loader from 'components/Loader/index';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { AppRoutes } from 'routes/routes.enums';
import { getAccount, getBorrowerByToken } from 'thunks/index';
import { getApplicationInfoForDashboardThunk } from 'thunks/getApplicationInfoForDashboard';
import { setAccount, setSignInStep, SignInStep } from 'actions/index';
import { resetAllSteps } from 'actions/stages';
import { useAppSelector } from 'hooks/useAppSelector';
import { applicationApi } from 'api/ApplicationApi';
import { marketingApi } from 'api/MarketingApi';
import { IS_CONCEPTUAL_HQ_ENABLED } from 'constants/constants';
import { getConceptualDeviceId } from 'routes/routes.utils';
import { PrequalificationVersion } from 'enums/ApplicationsVariables';
import { FeatureFlag } from 'enums/featureFlag.enums';

const PrivateRoute = () => {
  const navigator = useAppNavigate();
  const [isLoading, setIsLoading] = React.useState(true);
  const refreshToken = localStorage.getItem('refreshToken');
  const dispatch = useAppDispatch();
  const {
    borrower: { borrowerId },
  } = useAppSelector((state) => state);
  const { sessionInfo } = useAppSelector((state) => state.account);

  const updateDeviceId = async () => {
    if (IS_CONCEPTUAL_HQ_ENABLED) {
      const deviceId = await getConceptualDeviceId();

      if (deviceId) {
        marketingApi.updateDevice({ deviceId: deviceId });
      }
    }
  };

  const getInitialData = async () => {
    try {
      if (!refreshToken) {
        return;
      }

      const account = await dispatch(getAccount()).unwrap();

      const borrower = await dispatch(getBorrowerByToken()).unwrap();

      dispatch(setAccount({ email: account.email, phone: account.phones[0]?.value }));

      if (sessionInfo?.isEmailNotVerified) {
        navigator(AppRoutes.Login);
        dispatch(setSignInStep(SignInStep.EnterConfirmationCode));

        return;
      }

      const application = await dispatch(getApplicationInfoForDashboardThunk(borrower.id!)).unwrap();
      const borrowerApplication = Array.isArray(application) ? undefined : application;

      if (borrowerApplication?.prequalificationVersion === PrequalificationVersion.Second) {
        localStorage.setItem(FeatureFlag.IsNewPrequalificationEnabled, 'true');
        window.location.reload();

        return;
      }

      if (borrowerApplication?.id) {
        applicationApi.updateLoanPortalAccessTime(borrowerApplication.id);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    dispatch(resetAllSteps());
    getInitialData();
    updateDeviceId();

    // we need login User only once when page is loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <Loader />;
  }

  if (!refreshToken || !borrowerId) {
    return <Navigate to={AppRoutes.Login} />;
  }

  return <Outlet />;
};

export default PrivateRoute;
